import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

/** Response from POST /appsumo/oauth/exchange — the code is burned and
 *  the real license_key is stashed in Redis under the returned token,
 *  so the browser never holds the license key itself. */
export interface AppSumoExchangeResponse {
  activation_token: string;
  license_tier: number;
  is_new_license: boolean;
}

export interface AppSumoCompleteResponse {
  license_key: string;
  tier: number;
  organization_id: string;
}

// Compact license shape returned by the admin list endpoint. Mirrors
// LicenseSummary in cleanse_api/api/appsumo_api.py.
export interface AppSumoLicenseSummary {
  license_key: string;
  organization_id: string | null;
  organization_name: string | null;
  tier: number;
  status: 'pending' | 'active' | 'deactivated' | string;
  appsumo_user_id: string | null;
  last_event: string | null;
  last_event_at: string | null;
  last_applied_at: string | null;
  created_at: string;
}

export interface AppSumoLicenseListResponse {
  items: AppSumoLicenseSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface AppSumoLicenseEventSummary {
  event: string;
  tier: number | null;
  status: string | null;
  is_test: boolean;
  appsumo_updated_at: string | null;
  received_at: string;
}

export interface AppSumoLicenseDetail extends AppSumoLicenseSummary {
  license_redemption_url: string | null;
  license_change_plan_url: string | null;
  organization: {
    organization_id: string;
    name: string | null;
    owner_name: string | null;
    owner_email: string | null;
    credits_count: number;
    appsumo_tier: number | null;
    seats_paid: number;
    appsumo_last_refilled_at: string | null;
  } | null;
  recent_events: AppSumoLicenseEventSummary[];
}

export interface AttributeLicenseResponse {
  license_key: string;
  organization_id: string;
  organization_name: string | null;
  status: string;
}

/** One flagged AppSumo refund abuser. Mirrors AbuseFlagRow in
 *  cleanse_api/api/billing/appsumo.py — projected from the inert AUDIT
 *  ledger row the refund-abuse detector writes on the `deactivate` webhook. */
export interface AppSumoAbuseRow {
  organization_id: string;
  organization_name: string | null;
  license_key: string | null;
  tier: number | null;
  credits_granted: number;
  credits_used: number;
  /** 0..1 fraction of lifetime AppSumo grant spent before the refund. */
  used_pct: number;
  days_active: number | null;
  /** Which signals fired: 'heavy_use' | 'serial_refunder' | 'fast_burn' | 'high_absolute'. */
  abuse_flags: string[];
  is_serial_refunder: boolean;
  refunded_at: string | null;
  flagged_at: string;
}

/** Window for the activity view, in whole UTC days (today included). */
export const APPSUMO_ACTIVITY_DAYS = [7, 30, 90] as const;
export type AppSumoActivityDays = (typeof APPSUMO_ACTIVITY_DAYS)[number];

/** One licence bind, from the activation_audit ledger row. */
export interface AppSumoActivationRow {
  organization_id: string;
  organization_name: string | null;
  owner_email: string | null;
  license_key: string | null;
  tier: number | null;
  is_re_redemption: boolean;
  activated_at: string;
}

/** One AppSumo credit grant other than the activation itself. */
export interface AppSumoRefillRow {
  organization_id: string;
  organization_name: string | null;
  owner_email: string | null;
  credits: number;
  /** monthly_refill | one_time_release | upgrade | addon */
  operation: string;
  reason: string;
  tier: number | null;
  refilled_at: string;
}

/** One UTC day of the activity window, zero-filled. */
export interface AppSumoActivityDay {
  /** YYYY-MM-DD, a UTC day. */
  day: string;
  activations: number;
  refills: number;
  credits_refilled: number;
}

export interface AppSumoActivityTotals {
  activations_count: number;
  credits_refilled: number;
  refills_count: number;
  distinct_orgs: number;
}

/** GET /management/appsumo/activity. Totals cover the whole window; the
 *  lists are capped server-side, newest first. */
export interface AppSumoActivityResponse {
  window: string;
  hours: number;
  since: string;
  activations_count: number;
  credits_refilled: number;
  refills_count: number;
  distinct_orgs: number;
  credits_by_reason: Record<string, number>;
  activations: AppSumoActivationRow[];
  refills: AppSumoRefillRow[];
  timezone: string;
  /** One row per UTC day, oldest first. */
  daily: AppSumoActivityDay[];
  /** The same-length span right before the window. */
  previous: AppSumoActivityTotals | null;
}

export interface AppSumoAbuseListResponse {
  items: AppSumoAbuseRow[];
  total: number;
  limit: number;
  offset: number;
}

// ---------------------------------------------------------------------------
// AppSumo upgrade campaign (Oct 2026): Starter or Pro on top of the deal, at
// the AppSumo price ($19 / $39 a seat per month, monthly only).
// Mirrors cleanse_api/api/billing/appsumo_upgrade.py, mounted at /appsumo.
// ---------------------------------------------------------------------------

export type AppSumoUpgradePhase = 'open' | 'final_week' | 'closed';

export type AppSumoUpgradeReason =
  | 'not_appsumo'
  | 'already_subscribed'
  | 'closed';

/** The two plans the campaign sells. */
export type AppSumoUpgradePlanKey = 'pro' | 'starter';

/** One plan at the AppSumo price. Cents per seat, a month. */
export interface AppSumoUpgradePlan {
  key: AppSumoUpgradePlanKey;
  name: string;
  offer_cents: number;
  list_cents: number;
  /** Searches a month; null is no limit (Pro). */
  search_limit: number | null;
}

/** What a plan adds, keyed; the portal maps each key to the copy deck. */
export type AppSumoUpgradeGain =
  | 'search'
  | 'clu'
  | 'sequences'
  | 'agents'
  | 'slack'
  | 'reports'
  | 'crm'
  | 'api';

/** One row of what the plans add, as the server sends it: a key and the
 *  plans that include it. Typed loosely: a key or plan the portal has no
 *  words for yet is dropped (see `knownGains`), never shown blank. */
export interface AppSumoUpgradeGainRow {
  key: string;
  plans: string[];
}

/** What the deal gives today, and keeps giving under a plan. `null` = no
 *  limit. */
export interface AppSumoUpgradeKeeps {
  monthly_credits: number;
  seats: number;
  leads_per_list: number | null;
  max_lists: number | null;
  search_limit: number | null;
}

/** A limit that moves with Starter or Pro. `null` = unlimited. */
export interface AppSumoUpgradeLimitChange {
  key: string;
  current: number | null;
  next: number | null;
}

/**
 * GET /appsumo/upgrade/offer. `eligible: false` is an ordinary state, not an
 * error: `reason` says why. A 404 means the org is flagged out of
 * `appsumo_upgrade_v1`, which every surface reads as "no offer".
 *
 * The server decides everything here against the same entitlements resolver
 * the gates enforce (the prices, which gains a tier sees, which limits move),
 * so the portal renders it and never recomputes it.
 */
export interface AppSumoUpgradeOffer {
  eligible: boolean;
  reason: AppSumoUpgradeReason | null;
  phase: AppSumoUpgradePhase;
  ends_at: string;
  final_week_starts_at: string;
  days_left: number;
  appsumo_tier: number | null;
  tier_label: string | null;
  is_top_tier: boolean;
  members: number;
  /** Pro first, then Starter. */
  plans: AppSumoUpgradePlan[];
  /** In the order the page lists them. Empty for an ineligible org. */
  gains: AppSumoUpgradeGainRow[];
  /** Null for an ineligible org. */
  keeps: AppSumoUpgradeKeeps | null;
  /** Empty for an ineligible org. */
  limit_changes: AppSumoUpgradeLimitChange[];
  /** The plan the org already pays for at the AppSumo price, if any. */
  current_plan: AppSumoUpgradePlanKey | null;
  trial: { available: boolean; days: number; plan?: AppSumoUpgradePlanKey };
  call_url: string;
  preview: boolean;
  /** False for a member who isn't a workspace admin (checkout would 403).
   *  Absent from older responses, where it reads as true. */
  can_purchase?: boolean;
}

/** Dev-only preview params; the server refuses them in production. */
export interface AppSumoUpgradeOfferPreview {
  preview_tier?: number;
  preview_state?: string;
  preview_phase?: AppSumoUpgradePhase;
  preview_members?: number;
  preview_trial?: boolean;
}

/** Monthly only; a trial is Pro only. */
export interface AppSumoUpgradeCheckoutRequest {
  plan: AppSumoUpgradePlanKey;
  /** At least the member count; null takes the member count, or keeps the
   *  current seats when a Starter workspace moves to Pro. */
  seats: number | null;
  trial: boolean;
}

/** `action: 'checkout'` opens Stripe Checkout (`url` or `sessionId`);
 *  `'modified'` already switched the plan in place (Starter to Pro), and
 *  `url` is where to go next. */
export interface AppSumoUpgradeCheckoutResponse {
  url?: string;
  sessionId?: string;
  action?: string;
}

/** DEV only: the local API's preview tools (APPSUMO_PREVIEW_TOOLS=1), which
 *  put this workspace into a campaign persona. Absent elsewhere (404). */
export type AppSumoPreviewPlan =
  | 'appsumo'
  | 'pro'
  | 'starter'
  | 'free'
  | 'none';

export interface AppSumoPreviewWorkspace {
  org_exists: boolean;
  appsumo_tier: number | null;
  /** Null for a workspace the local API has no row for yet. */
  members: number | null;
  /** The balance, which can be fractional. Null as for `members`. */
  credits: number | null;
  plan: AppSumoPreviewPlan;
  has_snapshot: boolean;
}

export type AppSumoPreviewAction =
  | 't1-solo'
  | 't3-solo'
  | 't5-team'
  | 't7-power'
  | 'not-appsumo'
  | 'undo-purchase'
  | 'reset';

/** The tenant guard the billing calls send (see billing.feature.tsx): the
 *  API rejects a request whose token names another org. */
const expectedOrganization = (organizationId: string) => ({
  'X-Cleanlist-Expected-Org': organizationId,
});

export const appsumoApi = createApi({
  reducerPath: 'appsumoApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'AppSumoLicense',
    'AppSumoAbuse',
    'AppSumoUpgradeOffer',
    'AppSumoPreview',
  ],
  endpoints: (builder) => ({
    /** The campaign offer for the current org. Keyed by organization so a
     *  workspace switch never shows the last org's offer. */
    getAppSumoUpgradeOffer: builder.query<
      AppSumoUpgradeOffer,
      { organizationId: string; preview?: AppSumoUpgradeOfferPreview }
    >({
      query: ({ organizationId, preview }) => {
        const params = Object.fromEntries(
          Object.entries(preview ?? {}).filter(([, value]) => value != null)
        );
        return {
          url: '/appsumo/upgrade/offer',
          headers: expectedOrganization(organizationId),
          params: Object.keys(params).length ? params : undefined,
        };
      },
      providesTags: (_result, _error, { organizationId }) => [
        { type: 'AppSumoUpgradeOffer', id: organizationId },
      ],
    }),

    /** Stripe Checkout for Starter or Pro at the AppSumo price, or the move
     *  from Starter to Pro in place. Interactive org admins only; the server
     *  picks the price, never the browser. */
    createAppSumoUpgradeCheckout: builder.mutation<
      AppSumoUpgradeCheckoutResponse,
      AppSumoUpgradeCheckoutRequest & { organizationId: string }
    >({
      query: ({ organizationId, ...body }) => ({
        url: '/appsumo/upgrade/checkout',
        method: 'POST',
        headers: expectedOrganization(organizationId),
        body,
      }),
    }),

    /** DEV only (the preview panel): where this workspace stands locally. */
    getAppSumoPreviewWorkspace: builder.query<
      AppSumoPreviewWorkspace,
      { organizationId: string }
    >({
      query: ({ organizationId }) => ({
        url: '/appsumo/upgrade/preview',
        headers: expectedOrganization(organizationId),
      }),
      providesTags: (_result, _error, { organizationId }) => [
        { type: 'AppSumoPreview', id: organizationId },
      ],
    }),

    /** DEV only: put this workspace into a persona, undo a test purchase, or
     *  restore it. 409 with a message when refused. */
    runAppSumoPreviewAction: builder.mutation<
      AppSumoPreviewWorkspace,
      { organizationId: string; action: AppSumoPreviewAction }
    >({
      query: ({ organizationId, action }) => ({
        url: '/appsumo/upgrade/preview',
        method: 'POST',
        headers: expectedOrganization(organizationId),
        body: { action },
      }),
    }),

    // Unauthenticated — the AppSumo `code` is the credential. Called
    // immediately on /appsumo/activate mount.
    exchangeCode: builder.mutation<AppSumoExchangeResponse, { code: string }>({
      query: (body) => ({
        url: '/appsumo/oauth/exchange',
        method: 'POST',
        body,
      }),
    }),

    // Clerk-authenticated — called after the user signs in / signs up
    // and we have a Bearer token to attach the license to their org.
    completeActivation: builder.mutation<
      AppSumoCompleteResponse,
      { activation_token: string }
    >({
      query: (body) => ({
        url: '/appsumo/oauth/complete',
        method: 'POST',
        body,
      }),
    }),

    // Staff-only support dashboard endpoints (gate is on the server:
    // 403 unless Clerk email ends with @cleanlist.ai).
    adminListLicenses: builder.query<
      AppSumoLicenseListResponse,
      { q?: string; status?: string; limit?: number; offset?: number }
    >({
      query: ({ q, status, limit = 50, offset = 0 }) => ({
        url: '/management/appsumo/licenses',
        params: {
          q: q || undefined,
          status_filter: status || undefined,
          limit,
          offset,
        },
      }),
      providesTags: [{ type: 'AppSumoLicense', id: 'LIST' }],
    }),

    adminGetLicense: builder.query<AppSumoLicenseDetail, string>({
      query: (licenseKey) => `/management/appsumo/licenses/${licenseKey}`,
      providesTags: (_res, _err, licenseKey) => [
        { type: 'AppSumoLicense', id: licenseKey },
      ],
    }),

    // Staff-only: flagged AppSumo refund abusers (activate → burn → refund).
    adminListAbuse: builder.query<
      AppSumoAbuseListResponse,
      { q?: string; limit?: number; offset?: number }
    >({
      query: ({ q, limit = 50, offset = 0 }) => ({
        url: '/management/appsumo/abuse',
        params: { q: q || undefined, limit, offset },
      }),
      providesTags: [{ type: 'AppSumoAbuse', id: 'LIST' }],
    }),

    // Staff-only: licence activations + credit refills per UTC day.
    adminAppSumoActivity: builder.query<
      AppSumoActivityResponse,
      AppSumoActivityDays
    >({
      query: (days) => ({
        url: '/management/appsumo/activity',
        // The lists feed the expanded days; 2000 (the API cap) covers them.
        params: { days, limit: 2000 },
      }),
    }),

    // Staff-only: bind a license to the org owned by a user (by id or email).
    adminAttributeLicense: builder.mutation<
      AttributeLicenseResponse,
      {
        licenseKey: string;
        user_id?: string;
        email?: string;
        organization_id?: string;
      }
    >({
      query: ({ licenseKey, user_id, email, organization_id }) => ({
        url: `/management/appsumo/licenses/${licenseKey}/attribute`,
        method: 'POST',
        body: { user_id, email, organization_id },
      }),
      invalidatesTags: (_res, _err, { licenseKey }) => [
        { type: 'AppSumoLicense', id: licenseKey },
        { type: 'AppSumoLicense', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetAppSumoUpgradeOfferQuery,
  useCreateAppSumoUpgradeCheckoutMutation,
  useGetAppSumoPreviewWorkspaceQuery,
  useRunAppSumoPreviewActionMutation,
  useExchangeCodeMutation,
  useCompleteActivationMutation,
  useAdminListLicensesQuery,
  useAdminGetLicenseQuery,
  useAdminListAbuseQuery,
  useAdminAppSumoActivityQuery,
  useAdminAttributeLicenseMutation,
} = appsumoApi;
